import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2 } from "lucide-react";
import { MONTH_SHORT, monthName, monthShort, ZONES, ZONE_COLORS, fmt1, toYears, useSpotMonths } from "@/lib/spotHistory";

export const Route = createFileRoute("/admin/historik")({
  head: () => ({
    meta: [
      { title: "Historik – Admin – Elmarknad.se" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: Historik,
});

const tooltipStyle = {
  background: "var(--card)",
  border: "1px solid var(--border)",
  borderRadius: 12,
  fontSize: 12,
};

type Row = { label: string; SE1: number; SE2: number; SE3: number; SE4: number };

function SpotBarChart({ data }: { data: Row[] }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
        <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} />
        <YAxis fontSize={11} tickLine={false} axisLine={false} unit=" öre" width={60} />
        <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => `${fmt1(v)} öre/kWh`} />
        <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
        {ZONES.map((z) => (
          <Bar key={z} dataKey={z} fill={ZONE_COLORS[z]} radius={[3, 3, 0, 0]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function Historik() {
  const { data: months, isLoading, error } = useSpotMonths();
  const years = useMemo(() => toYears(months ?? []), [months]);
  const [selected, setSelected] = useState("");
  useEffect(() => {
    if (!selected && years[0]) setSelected(String(years[0].year));
  }, [years, selected]);
  const year = years.find((y) => String(y.year) === selected);

  const yearData: Row[] = [...years].reverse().map((y) => ({ label: String(y.year), SE1: y.SE1, SE2: y.SE2, SE3: y.SE3, SE4: y.SE4 }));
  const monthData = (year?.months ?? []).map((m) => ({
    label: monthShort(m.month),
    name: monthName(m.month),
    SE1: m.SE1, SE2: m.SE2, SE3: m.SE3, SE4: m.SE4, avg: m.avg, complete: m.complete,
  }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Historik</h1>
        <p className="text-sm text-muted-foreground">Genomsnittligt spotpris (öre/kWh exkl. moms) per år och månad, SE1–SE4.</p>
      </div>

      {isLoading && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Laddar spotpriser…</div>}
      {error && <p className="text-sm text-destructive">Kunde inte hämta spotpriser.</p>}

      {years.length > 0 && (
        <>
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle>Årsmedel per elområde</CardTitle></CardHeader>
            <CardContent className="h-[320px] pl-0"><SpotBarChart data={yearData} /></CardContent>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>År</TableHead>
                    {ZONES.map((z) => <TableHead key={z} className="text-right">{z}</TableHead>)}
                    <TableHead className="text-right">Sverige</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {years.map((y) => (
                    <TableRow key={y.year} className="cursor-pointer" data-state={String(y.year) === selected ? "selected" : undefined} onClick={() => setSelected(String(y.year))}>
                      <TableCell className="font-medium">
                        {y.year}
                        {(y.months.length < 12 || y.months.some((m) => !m.complete)) && <span className="ml-1 text-xs text-muted-foreground">(hittills)</span>}
                      </TableCell>
                      {ZONES.map((z) => <TableCell key={z} className="text-right tabular-nums">{fmt1(y[z])}</TableCell>)}
                      <TableCell className="text-right font-semibold tabular-nums">{fmt1(y.avg)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between gap-3">
              <CardTitle>Månadsmedel {selected}</CardTitle>
              <Select value={selected} onValueChange={setSelected}>
                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent>{years.map((y) => <SelectItem key={y.year} value={String(y.year)}>{y.year}</SelectItem>)}</SelectContent>
              </Select>
            </CardHeader>
            <CardContent className="h-[320px] pl-0"><SpotBarChart data={monthData} /></CardContent>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Månad</TableHead>
                    {ZONES.map((z) => <TableHead key={z} className="text-right">{z}</TableHead>)}
                    <TableHead className="text-right">Sverige</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {monthData.map((m) => (
                    <TableRow key={m.name}>
                      <TableCell className="font-medium">{m.name}{!m.complete && <span className="ml-1 text-xs text-muted-foreground">(hittills)</span>}</TableCell>
                      {ZONES.map((z) => <TableCell key={z} className="text-right tabular-nums">{fmt1(m[z])}</TableCell>)}
                      <TableCell className="text-right font-semibold tabular-nums">{fmt1(m.avg)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
